import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { View } from 'react-native';
import { AlertTile, FocusableFlatList, TWCAccessibleContent, TWCText } from '@components';
import { useRecoilState } from 'recoil';

import { alertDetailsPageTestIDs, currentWeatherConditionsPageTestIDs, Theme } from '@constants';
import { useContextNodeHandle, useContextReference, useNodeHandle } from '@hooks';
import { navigationService } from '@services';
import { AlertsHeadline, AlertType, screenReaderEnabledState } from '@store';

import { styles } from './styles';

const tPath = 'currentWeatherConditionsPage';

type CurrentWeatherConditionsBottomRow = {
  alerts: AlertsHeadline[];
  nextFocusUp: unknown;
  weatherAlertsHeaderHandle: unknown;
};

export const CurrentWeatherConditionsBottomRow: FC<CurrentWeatherConditionsBottomRow> = ({
  alerts,
  nextFocusUp,
  weatherAlertsHeaderHandle,
}) => {
  const { t } = useTranslation('translation', { keyPrefix: tPath });
  const [screenReaderEnabled] = useRecoilState(screenReaderEnabledState);

  const numberOfAlerts = alerts.length;

  const alertListReference = useContextReference(currentWeatherConditionsPageTestIDs.alertTilesList);

  const alertListHandle = useNodeHandle(alertListReference);
  const widgetReference = useContextReference(currentWeatherConditionsPageTestIDs.currentWeatherConditionsWidget);
  const widgetHandle = useContextNodeHandle(currentWeatherConditionsPageTestIDs.currentWeatherConditionsWidget);
  const onAlertPress = (index: number) => {
    navigationService.navigate(alertDetailsPageTestIDs.alertDetailsPage, {
      index,
      alerts,
    });
  };

  return (
    <View style={styles.bottomRowWrapper}>
      <View style={styles.weatherAlertsHeaderWrapper}>
        <TWCAccessibleContent
          disabledScreenreaderFocus={widgetReference}
          nextFocusDown={alertListHandle}
          nextFocusLeft={null}
          nextFocusRight={null}
          nextFocusUp={nextFocusUp}
          testID={currentWeatherConditionsPageTestIDs.weatherAlertsHeaderFrame}
        >
          <View style={styles.twcAccessibleContentContainer}>
            <TWCText testID={currentWeatherConditionsPageTestIDs.weatherAlertsHeaderTitle} variant={'regularText30'}>
              {t('weatherAlertsHeader.title')}
            </TWCText>
            <TWCText
              accessibilityHint={t(
                numberOfAlerts
                  ? 'weatherAlertsHeader.alertsAccessibilityHint'
                  : 'weatherAlertsHeader.noAlertsAccessibilityHint',
              )}
              accessibilityLabel={
                numberOfAlerts
                  ? t('weatherAlertsHeader.alertsAccessibilityLabel', {
                      alerts: numberOfAlerts,
                    })
                  : t('weatherAlertsHeader.noAlertsAccessibilityLabel')
              }
              testID={currentWeatherConditionsPageTestIDs.weatherAlertsNumberOfAlerts}
              variant={'regularText30Transparent'}
            >{` (${numberOfAlerts})`}</TWCText>
          </View>
        </TWCAccessibleContent>
      </View>

      {numberOfAlerts ? (
        <View style={styles.weatherAlertsContentWrapper}>
          <FocusableFlatList
            data={alerts}
            focusPositions={3}
            horizontal
            keyExtractor={(alert: AlertType) => alert.detailKey}
            nextFocusUp={screenReaderEnabled ? weatherAlertsHeaderHandle : widgetHandle}
            paddingEnd={Theme.px(186)}
            paddingStart={Theme.px(90)}
            ref={alertListReference}
            renderItem={(alert: AlertType, index: number) => (
              <AlertTile.API
                eventDescription={alert?.eventDescription}
                expireTimeUTC={alert.expireTimeUTC}
                index={index}
                issueTimeLocalTimeZone={alert.issueTimeLocalTimeZone}
                testID={`${currentWeatherConditionsPageTestIDs.alertTile}-${index}`}
                totalAlerts={numberOfAlerts}
                onPress={() => onAlertPress(index)}
              />
            )}
            showsHorizontalScrollIndicator={false}
            testID={currentWeatherConditionsPageTestIDs.alertTilesList}
            wrapItem={false}
          />
        </View>
      ) : null}
    </View>
  );
};
